import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { SPRING } from "../lib/motion";
import { paintedFromPrerender } from "../lib/prerender";
import GetApp from "../ui/GetApp";
import DeviceIphone from "../ui/DeviceIphone";
import { ShotImage } from "../ui/Screen";
import { useTheme } from "../theme/ThemeProvider";
import { shot } from "../data/shots";
import { Check } from "../ui/icons";
/*
 * The Material Design Icons set, the same glyphs the owner app draws through
 * @expo/vector-icons' MaterialCommunityIcons. Path data only: four imports,
 * four strings in the bundle.
 */
import { mdiAccountPlus, mdiCalendarAlert, mdiCashMultiple, mdiChartBar } from "@mdi/js";
import "./hero.css";

const LINES = ["Run your gym", "from one screen."];

/*
 * Facts, not fabricated social proof. A landing page normally fills this strip
 * with star ratings and customer logos; this product has neither yet, and a
 * page that quotes prices is the last place to invent them. A strip of its own
 * under the first screen, so the first screen carries one message.
 */
/* "Free", not how long: the trial length is on the pricing cards. */
const TRUST = [
  "Free to start",
  "No card needed",
  "A PDF receipt for every payment",
  "Trainers sign in free",
  "Export your data any time",
];

/*
 * What an owner's phone actually hears from the app, word for word as the
 * backend writes it (reminder.controller.js, staffActivity.js,
 * notification.controller.js, club.controller.js). Join requests, member
 * questions and reviews are left out: they come from the member app, and the
 * owner app hides them until that is live (club-frontend constants/features.ts,
 * MEMBER_APP_LIVE). The fold says only what the product says today. Names and
 * amounts match the demo club in the screenshots.
 */
const EVENTS = [
  {
    id: "nudge",
    tone: "brand",
    icon: mdiCashMultiple,
    title: "Reminders ready to send",
    sub: "5 members are waiting. Open Reminders and tap Send now.",
    short: "5 members are waiting",
  },
  {
    id: "added",
    tone: "neutral",
    icon: mdiAccountPlus,
    title: "New member",
    sub: "Ravi added Aarav Sharma.",
    short: "Ravi added Aarav Sharma",
  },
  {
    id: "expiring",
    tone: "warn",
    icon: mdiCalendarAlert,
    title: "Membership expiring",
    sub: "Rohan Nair's membership ends on 2026-08-24.",
    short: "Rohan Nair, ends 24 Aug",
  },
  {
    id: "summary",
    tone: "ok",
    icon: mdiChartBar,
    title: "Today at your club",
    sub: "Collected ₹12,600 today. 4 members still owe.",
    short: "Collected ₹12,600 today",
  },
];

/* How long ago each position in the stack arrived, as a phone would print it. */
const AGES = ["now", "1m"];

/* Two on show, beside the phone rather than on it, so the screen stays readable. */
const VISIBLE = 2;
/* One more card than is ever on show: the one waiting below to come round. */
const SLOTS = VISIBLE + 1;
/* The first arrival comes quickly so the reader sees the stack is live. */
const FIRST_MS = 1400;
const EVERY_MS = 2800;

export default function Hero() {
  const reduce = useReducedMotion();
  const { theme } = useTheme();
  // Already on screen from the prerendered HTML: start every entrance at its
  // resting value rather than blanking what the reader is looking at.
  const [settled] = useState(paintedFromPrerender);
  const still = reduce || settled;

  return (
    <section className="hero">
      <div className="container container--wide hero__grid">
        <div className="hero__copy">
          <h1 className="hero__headline">
            {LINES.map((line, i) => (
              <span className="hero__line" key={line}>
                <motion.span
                  className="hero__line-inner"
                  initial={still ? false : { y: "105%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.08 + i * 0.09 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          {/*
           * Thirteen words. The rest of what this line used to say (send
           * reminders yourself or automatically, it shows what needs you
           * today) is said by the day below, at 9:30 pm and 6:00 am.
           */}
          <motion.p
            className="t-lead hero__lead"
            initial={still ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...SPRING.ui, delay: 0.38 }}
          >
            Members, money, QR check-in and reminders, on your phone or any browser.
          </motion.p>

          <motion.div
            className="hero__actions"
            initial={still ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...SPRING.ui, delay: 0.48 }}
          >
            <GetApp />
          </motion.div>
        </div>

        <div className="hero__stage">
          <motion.div
            className="hero__phone"
            initial={still ? false : { opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.12 }}
          >
            <DeviceIphone>
              <ShotImage
                slug="today"
                appearance={theme}
                eager
                alt={`The Today screen. ${shot("today").inFrame}`}
              />
            </DeviceIphone>
          </motion.div>

          <Feed reduce={reduce} />
        </div>
      </div>

      <div className="hero__facts">
        <ul className="container container--wide hero__trust">
          {TRUST.map((item) => (
            <li className="hero__trust-item" key={item}>
              <Check />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * The notification stack beside the phone.
 *
 * A new push lands on top every few seconds and the older one drops off the
 * bottom. On a wide screen the two sit in a column to the left of the phone,
 * touching only its edge; on a phone they collapse into one card over the top
 * of the screen, with the older one peeking out underneath, the way a lock
 * screen groups them.
 *
 * Only reduced motion stands it still. The prerendered snapshot used to stop it
 * too, which froze it on every direct visit to the live site (every page there
 * is prerendered) and left it moving only after an in-app navigation. Now the
 * snapshot paints the first three, and the clock starts once the bundle runs.
 *
 * Three cards are mounted for good and take turns: two on show and one out
 * of sight below, waiting to come round to the top. Cards that mount and
 * unmount through AnimatePresence froze half-way on some ticks, sitting on
 * top of each other in one slot; slots that only ever change position have
 * nothing to freeze.
 */
function Feed({ reduce }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.2 });
  const compact = useMedia("(max-width: 960px)");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduce || !inView) return undefined;
    let id = 0;
    const next = (wait) => {
      id = window.setTimeout(() => {
        // A hidden tab skips the beat rather than stopping the clock, so the
        // stack is still live when the reader comes back.
        if (document.visibilityState === "visible") setTick((t) => t + 1);
        next(EVERY_MS);
      }, wait);
    };
    next(FIRST_MS);
    return () => window.clearTimeout(id);
  }, [reduce, inView]);

  const place = (age) => {
    if (age >= VISIBLE) {
      // Gone: dropped below the last card and faded, ready to come round.
      const last = place(VISIBLE - 1);
      return { ...last, y: last.y + (compact ? 6 : 24), opacity: 0, filter: "blur(4px)" };
    }
    // Cards stay opaque at every depth: a see-through card let the phone's
    // own numbers read through it. Depth is the content dimming (hero.css).
    return compact
      ? { y: age * 11, scale: 1 - age * 0.05, opacity: 1, filter: "blur(0px)" }
      : { y: age * 86, scale: 1, opacity: 1, filter: "blur(0px)" };
  };

  const slots = Array.from({ length: SLOTS }, (_, slot) => {
    // The newest arrival this slot has taken, and how long ago that was.
    const age = (((tick - slot) % SLOTS) + SLOTS) % SLOTS;
    const n = tick - age;
    const event = EVENTS[((n % EVENTS.length) + EVENTS.length) % EVENTS.length];
    return { slot, age, n, event };
  });

  const spring = { type: "spring", stiffness: 260, damping: 26, mass: 0.9 };
  const fade = { duration: 0.35, ease: [0.16, 1, 0.3, 1] };

  return (
    <div
      className="hero__feed"
      ref={ref}
      aria-hidden="true"
      data-compact={compact || undefined}
    >
      {slots.map(({ slot, age, n, event }) => {
        const at = place(age);
        /*
         * The arrival drops in from above the stack. As keyframes, so it
         * replays every time this slot comes round to the top; the first
         * paint (initial={false}) lands straight on the last frame.
         */
        const animate =
          age === 0 && !reduce
            ? {
                y: [-36, at.y],
                scale: [0.92, at.scale],
                opacity: [0, at.opacity],
                filter: ["blur(6px)", at.filter],
              }
            : at;
        const transition = reduce
          ? { duration: 0 }
          : age >= VISIBLE
            ? { duration: 0.32, ease: [0.4, 0, 1, 1] }
            : // Tweened, not sprung: a spring overshoots, and blur below
              // zero is not a value a browser will paint.
              { ...spring, opacity: fade, filter: fade };
        return (
          <motion.div
            key={slot}
            className="hero__note"
            style={{ zIndex: SLOTS - age }}
            data-age={Math.min(age, VISIBLE - 1)}
            initial={false}
            animate={animate}
            transition={transition}
          >
            <NoteContent
              key={`${slot}:${n}`}
              event={event}
              time={AGES[age] ?? ""}
              arrive={age === 0 && !reduce && tick > 0}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

/**
 * One notification's insides. Keyed by arrival, so a slot coming round to the
 * top remounts this and plays the arrival: the icon tile pops a beat after the
 * card lands, and the words rise in under it. The card's own move is the
 * headline; this is the follow-through that makes it read as something
 * arriving rather than a box sliding.
 */
function NoteContent({ event, time, arrive }) {
  const pop = arrive ? { scale: 0.4, opacity: 0 } : false;
  const rise = arrive ? { y: 8, opacity: 0 } : false;
  return (
    <span className="hero__note-inner">
      <motion.span
        className={`hero__note-tile hero__note-tile--${event.tone}`}
        initial={pop}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 420, damping: 18, delay: 0.14 }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d={event.icon} />
        </svg>
      </motion.span>
      <motion.span
        className="hero__note-body"
        initial={rise}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.08 }}
      >
        <span className="hero__note-app">
          Varzish Mate
          <span className="hero__note-time">{time}</span>
        </span>
        <span className="hero__note-title">
          {event.title}
          <span className="hero__note-time hero__note-time--inline">{time}</span>
        </span>
        <span className="hero__note-sub">{event.sub}</span>
        <span className="hero__note-short">{event.short}</span>
      </motion.span>
    </span>
  );
}

/** A media query as state, so the stack can change shape on a resize. */
function useMedia(query) {
  const get = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}
